import { useRef, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { parseColor } from '@/components/grid/color-utils'
import { resolveColor } from './resolve-color'
import { cn } from '@/lib/utils'

// ─── Palette: each sub-array is a vertical column (hue group), light → dark ───

const PALETTE_COLUMNS: string[][] = [
  // Grays
  ['#000000', '#262626', '#525252', '#737373', '#a3a3a3', '#d4d4d4', '#ffffff'],
  // Reds
  ['#450a0a', '#7f1d1d', '#b91c1c', '#dc2626', '#ef4444', '#f87171', '#fca5a5'],
  // Oranges
  ['#431407', '#7c2d12', '#c2410c', '#ea580c', '#f97316', '#fb923c', '#fdba74'],
  // Yellows
  ['#422006', '#713f12', '#a16207', '#ca8a04', '#eab308', '#facc15', '#fde047'],
  // Greens
  ['#052e16', '#14532d', '#15803d', '#16a34a', '#22c55e', '#4ade80', '#86efac'],
  // Teals
  ['#042f2e', '#134e4a', '#0f766e', '#0d9488', '#14b8a6', '#2dd4bf', '#5eead4'],
  // Blues
  ['#172554', '#1e3a8a', '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'],
  // Purples
  ['#2e1065', '#4c1d95', '#6d28d9', '#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd'],
  // Pinks
  ['#500724', '#831843', '#be185d', '#db2777', '#ec4899', '#f472b6', '#f9a8d4'],
]

// ─── Helpers ───

function hexFromRgb(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.round(Math.max(0, Math.min(255, n)))
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function composeColor(hex: string, opacity: number): string {
  if (opacity >= 100) return hex
  if (opacity <= 0) return 'transparent'
  const [r, g, b] = parseColor(hex)
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${(opacity / 100).toFixed(2)})`
}

/**
 * The colour as the picker shows it. A theme colour (the chart background's `hsl(var(--chart-canvas))`)
 * resolves to what it paints now, so the picker opens on it rather than on black.
 */
function decomposeColor(color: string): { hex: string; opacity: number } {
  if (color === 'transparent') return { hex: '#000000', opacity: 0 }
  const [r, g, b, a] = resolveColor(color)
  return { hex: hexFromRgb(r, g, b), opacity: Math.round(a * 100) }
}

// ─── Component ───

interface ColorSwatchPickerProps {
  value: string
  onChange: (color: string) => void
  className?: string
  title?: string
  /** Hide the opacity slider and always emit plain hex (opacity handled elsewhere). */
  hideOpacity?: boolean
  /** `data-companion-label` for the trigger swatch, so guided walkthroughs can point at it. */
  companionLabel?: string
}

export function ColorSwatchPicker({
  value,
  onChange,
  className,
  title,
  hideOpacity = false,
  companionLabel,
}: ColorSwatchPickerProps) {
  const nativeRef = useRef<HTMLInputElement>(null)
  // Read when the picker opens: a theme colour paints differently once the theme changes, and the
  // closed swatch paints `value` itself.
  const [open, setOpen] = useState(false)
  const { hex, opacity } = useMemo(
    () => (open ? decomposeColor(value) : { hex: '#000000', opacity: 100 }),
    [open, value],
  )

  // When opacity is managed by the host, never fold alpha into the color.
  const effectiveOpacity = hideOpacity ? 100 : opacity

  const handleSwatchClick = (swatchHex: string) => {
    onChange(composeColor(swatchHex, effectiveOpacity))
  }

  const handleOpacityChange = (newOpacity: number) => {
    onChange(composeColor(hex, newOpacity))
  }

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(composeColor(e.target.value, effectiveOpacity))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn('h-6 w-6 rounded border border-border cursor-pointer shrink-0', className)}
          style={{ backgroundColor: value }}
          title={title}
          data-companion-label={companionLabel}
        />
      </PopoverTrigger>
      {/* z-[1100]: the picker portals to <body>, so it must sit above BOTH the
          maximized mosaic tile (z-100, mosaic-overrides.css) AND the chart
          settings / indicator drawer panels (z-[1000]) it opens from — those
          also portal to <body>, so a lower z leaves the picker hidden behind
          them. */}
      <PopoverContent className="w-auto p-3 z-[1100]" align="start" sideOffset={6}>
        {/* Swatch grid */}
        <div className="flex gap-0.5">
          {PALETTE_COLUMNS.map((column, colIdx) => (
            <div key={colIdx} className="flex flex-col gap-0.5">
              {column.map((color, rowIdx) => (
                <button
                  key={rowIdx}
                  type="button"
                  className={cn(
                    'h-5 w-5 rounded-sm border cursor-pointer transition-transform hover:scale-125 hover:z-10',
                    hex.toLowerCase() === color.toLowerCase()
                      ? 'border-white ring-1 ring-primary'
                      : 'border-transparent',
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => handleSwatchClick(color)}
                  // One popover is open at a time, so a shared label is safe;
                  // a walkthrough points at the first swatch as the exemplar.
                  data-companion-label="color-swatch-option"
                />
              ))}
            </div>
          ))}
        </div>

        {/* Custom color button */}
        <div className="mt-2">
          <button
            type="button"
            className="h-5 w-5 rounded-sm border border-dashed border-muted-foreground/50 flex items-center justify-center text-muted-foreground hover:border-foreground hover:text-foreground transition-colors cursor-pointer"
            onClick={() => nativeRef.current?.click()}
            title="Pick custom color"
            data-companion-label="color-swatch-custom"
          >
            <Plus className="h-3 w-3" />
          </button>
          <input
            ref={nativeRef}
            type="color"
            value={hex}
            onChange={handleNativeChange}
            className="sr-only"
            tabIndex={-1}
          />
        </div>

        {/* Opacity slider */}
        {!hideOpacity && (
          <div className="mt-3">
            <span className="text-[11px] text-muted-foreground">Opacity</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="range"
                min={0}
                max={100}
                value={opacity}
                onChange={(e) => handleOpacityChange(Number(e.target.value))}
                className="flex-1 h-1.5 accent-primary cursor-pointer"
              />
              <span className="text-xs tabular-nums w-7 text-right">{opacity}</span>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
